import { useQuery } from '@tanstack/react-query';

import { Usage } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { useTelemetryService } from '../../../telemetry/hooks';
import { UsageDataParams } from '../types';

/**
 * Custom hook to fetch usage data from the telemetry service using React Query
 *
 * Fetches resource usage data based on the provided parameters and manages
 * loading, error, and caching states automatically.
 *
 * @param params Parameters for fetching usage data (dates, granularity, grouping, filters)
 * @returns Object containing usage data, loading state, error, and refetch function
 */
export function useUsageData(params: UsageDataParams) {
  const {
    startTime,
    endTime,
    granularity,
    groupBy,
    selectedServices,
    selectedWorkspaces,
    selectedUsers,
    selectedTags,
  } = params;
  const TelemetryService = useTelemetryService();

  const query = useQuery<Usage[], Error>(
    // Query key - refetch when any of these change
    // Fixed: Use stable string key to prevent cache misses from array mutation
    [
      'usageData',
      {
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        granularity,
        groupBy,
        servicesKey: selectedServices.length > 0 ? [...selectedServices].sort().join(',') : 'none',
        workspacesKey: selectedWorkspaces.length > 0 ? [...selectedWorkspaces].sort().join(',') : 'none',
        usersKey: selectedUsers.length > 0 ? [...selectedUsers].sort().join(',') : 'none',
        tagsKey: selectedTags.length > 0 ? [...selectedTags].sort().join(',') : 'none',
      },
    ],
    // Query function
    async () => {
      const startTimeISO = startTime.toISOString();
      const endTimeISO = endTime.toISOString();

      // Build combined filter string from all filter types
      let filterString: string | undefined = undefined;
      const filterParts: string[] = [];

      // Service filter
      if (selectedServices.length > 0) {
        const serviceFilter = selectedServices.map((svc) => `service = '${svc}'`).join(' OR ');
        filterParts.push(serviceFilter);
      }

      // Workspace filter
      if (selectedWorkspaces.length > 0) {
        const workspaceFilter = selectedWorkspaces.map((ws) => `workspace = '${ws}'`).join(' OR ');
        filterParts.push(workspaceFilter);
      }

      // User filter
      if (selectedUsers.length > 0) {
        const userFilter = selectedUsers.map((user) => `user = '${user}'`).join(' OR ');
        filterParts.push(userFilter);
      }

      // Tag filter
      if (selectedTags.length > 0) {
        const tagFilter = selectedTags.map((tag) => `tag = '${tag}'`).join(' OR ');
        filterParts.push(tagFilter);
      }

      // Combine all filter parts with AND (wrap OR expressions in parens when combining)
      if (filterParts.length > 0) {
        if (filterParts.length === 1) {
          filterString = filterParts[0];
        } else {
          filterString = filterParts.map((part) => (part.includes(' OR ') ? `(${part})` : part)).join(' AND ');
        }
      }

      const response = await TelemetryService.calculateResourceUsage({
        startTime: startTimeISO,
        endTime: endTimeISO,
        granularity,
        groupBy,
        ...(filterString && { filter: filterString }),
      });

      return response.usage || [];
    },
    {
      // Keep data fresh for 30 minutes
      staleTime: 1000 * 60 * 30,
      // Cache for 30 minutes
      cacheTime: 1000 * 60 * 30,
      // Retry failed requests twice
      retry: 2,
      // Retry delay increases exponentially
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Only fetch when component is mounted
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect
      refetchOnReconnect: false,
      // Handle errors
      onError: (error: Error) => {
        console.error('[useUsageData] Error fetching usage data:', error);
      },
    }
  );

  return {
    usageData: query.data,
    isLoading: query.isLoading,
    error: query.error || undefined,
    refetch: query.refetch,
  };
}
